import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { UserIcon, LockIcon } from '../icons/Icons'
import { useDemoAuth } from '../../contexts/DemoAuthContext'

const AuthForm: React.FC = () => {
  const navigate = useNavigate()
  const { login } = useDemoAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Use demo auth system
    const success = login(email, password)
    if (success) {
      if (email === 'admin@gov.in') {
        navigate('/admin')
      } else {
        navigate('/dashboard')
      }
    } else {
      setError('Invalid credentials. Use demo accounts.')
    }
  }

  const fillDemoCredentials = (type: 'admin' | 'user') => {
    if (type === 'admin') {
      setEmail('admin@gov.in')
      setPassword('admin123')
    } else {
      setEmail('user@gmail.com')
      setPassword('user123')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8 relative">
      {/* Background image with blur */}
      <div 
        className="absolute inset-0 bg-cover bg-center z-0 filter blur-sm"
        style={{ backgroundImage: "url('/login.webp')" }}
      ></div>
      
      {/* Overlay to make content more readable */}
      <div className="absolute inset-0 bg-black bg-opacity-60 z-0"></div>
      
      {/* Login form container with slight blur effect */}
      <div className="relative z-10 max-w-md w-full space-y-6 sm:space-y-8 bg-white bg-opacity-80 rounded-2xl shadow-xl p-6 sm:p-8 backdrop-blur-[2px] border border-white border-opacity-30">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-3 sm:mb-4">
            <img src="/logo.png" alt="Kriyate Logo" className="w-14 h-14 sm:w-16 sm:h-16 rounded-full" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Kriyate
          </h2>
          <p className="text-gray-700 mb-5 sm:mb-6 text-sm sm:text-base px-2">
            Report and track civic issues in your area
          </p>
        </div>

        {/* Demo Credentials Buttons */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-5 sm:mb-6">
          <h3 className="text-sm font-semibold text-blue-900 mb-2 sm:mb-3">Demo Accounts</h3>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button
              onClick={() => fillDemoCredentials('user')}
              className="flex-1 px-3 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Fill User Login
            </button>
            <button
              onClick={() => fillDemoCredentials('admin')}
              className="flex-1 px-3 py-2 text-xs sm:text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
            >
              Fill Admin Login
            </button>
          </div>
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="space-y-5 sm:space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-800 mb-1.5 sm:mb-2">
              Email Address
            </label>
            <div className="relative">
              <UserIcon className="absolute left-3 top-3 sm:top-3.5 w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base bg-white bg-opacity-90"
                placeholder="Enter your email"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-800 mb-1.5 sm:mb-2">
              Password
            </label>
            <div className="relative">
              <LockIcon className="absolute left-3 top-3 sm:top-3.5 w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base bg-white bg-opacity-90"
                placeholder="Enter your password"
                required
              />
            </div>
          </div>

          {error && (
            <div className="text-red-600 text-sm text-center bg-red-50 border border-red-200 rounded-lg p-2.5 sm:p-3">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-2.5 sm:py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200 text-sm sm:text-base shadow-md"
          >
            Sign In
          </button>
        </form>

        {/* Demo Info */}
        <div className="mt-5 sm:mt-6 text-xs sm:text-sm text-gray-700 text-center px-2">
          <p className="mb-1.5 sm:mb-2"><strong>User Account:</strong> user@gmail.com / user123</p>
          <p><strong>Admin Account:</strong> admin@gov.in / admin123</p>
        </div>
      </div>
    </div>
  )
}

export default AuthForm